import React, { useContext, useEffect, useState } from 'react';
import Orders from '../../components/user/Orders';
import SubmitButton from '../../components/SubmitButton';
import { RouteComponentProps, withRouter } from 'react-router';
import Title from '../../components/Title';
import { axiosKK } from '../../services/networkRequest';
import { PreviousOrderListResponse, RemovalOrdersResponse, Order } from '../../models/APIModels';
import UserContext from '../../contexts/UserContext';
import OrderEditDeliveryDetailsModal from '../../components/edit/OrderEditDeliveryDetailsModal';
import { OrderSelectionModal } from '../../components/OrderSelectionModal';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/solid';

function DashboardComponent({ history }: RouteComponentProps): JSX.Element {
  const [orders, setOrders] = useState<Order[]>([]);
  const [showOrderSelectionModal, setShowOrderSelectionModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const { user } = useContext(UserContext);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Fetch both regular orders and removal orders
    Promise.all([
      axiosKK.get<PreviousOrderListResponse>('/order/previous'),
      axiosKK.get<RemovalOrdersResponse>('/removals/orders').catch(() => ({ data: { orders: [] } }))
    ]).then(([regularOrdersResult, removalOrdersResult]) => {
      // Merge both types of orders
      const allOrders = [
        ...regularOrdersResult.data.previousOrders,
        ...removalOrdersResult.data.orders
      ];
      
      // Sort by creation date (most recent first)
      setOrders(allOrders.sort((a, b) => b.createdAt - a.createdAt));
    }).catch((error) => {
      console.error('Error fetching orders:', error);
      // If regular orders fail, still set empty array
      setOrders([]);
    });
  }, []);

  const eligibleOrdersForReturn = orders.filter(
    (order) =>
      order.storageRequired && !order.suitcaseOrder && !order.deliveryDate,
  );

  const handleReturnItems = () => {
    if (eligibleOrdersForReturn.length > 0) {
      setShowOrderSelectionModal(true);
    } else {
      history.push('/');
    }
  };

  const handleOrderSelection = (order: Order) => {
    setSelectedOrder(order);
    setShowOrderSelectionModal(false);
    setShowDeliveryModal(true);
  };

  const handleDeliveryModalClose = () => {
    setShowDeliveryModal(false);
    setSelectedOrder(null);
  };

  const handleDeliveryUpdate = (updatedOrder: Order) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order.id === updatedOrder.id ? updatedOrder : order,
      ),
    );
    handleDeliveryModalClose();
  };

  return (
    <>
      <Title>Customer Dashboard</Title>
      {user && <p className="grey">Welcome back, {user?.firstName}</p>}
      <div className="dashboard-all-box-container customer-dashboard">
        <section onClick={() => history.push('/')} className="each-box">
          <div className="content">
            <img
              className="static-state"
              src="/img/box-icon-black.png"
              alt=""
            />
            <img
              className="rollover-state"
              src="/img/box-icon-white.png"
              alt=""
            />
            <h3>Make New Bookings!</h3>
          </div>
          <SubmitButton>Book Now</SubmitButton>
        </section>
        <section onClick={() => history.push('/profile')} className="each-box">
          <div className="content">
            <img
              className="static-state"
              src="/img/ship-icon-black.png"
              alt=""
            />
            <img
              className="rollover-state"
              src="/img/ship-icon-white.png"
              alt=""
            />
            <h3>Name, Address and Payment Details</h3>
          </div>
          <SubmitButton>Manage Profile</SubmitButton>
        </section>
        <section onClick={() => history.push('/payments')} className="each-box">
          <div className="content">
            <img className="static-state" src="/img/dummy-item.png" alt="" />
            <img
              className="rollover-state"
              src="/img/dummy-item-white.png"
              alt=""
            />
            <h3>Recurring Payments and Transactions</h3>
          </div>
          <SubmitButton>Track My Payments</SubmitButton>
        </section>
        {eligibleOrdersForReturn.length > 0 && (
          <section onClick={handleReturnItems} className="each-box">
            <div className="content">
              <div className="icon-wrapper static-state">
                <ArrowUturnLeftIcon strokeWidth={2.5} />
              </div>
              <div className="icon-wrapper rollover-state">
                <ArrowUturnLeftIcon strokeWidth={2.5} />
              </div>
              <h3>Request Return of Your Items</h3>
            </div>
            <SubmitButton>Return My Items</SubmitButton>
          </section>
        )}
      </div>

      <div className="line" />

      <Title>Your Recent Orders</Title>
      <p className="grey">
        Please note that only orders that are not processed are shown below. To
        view all orders, please naviagte to <i>Track my Order</i>.
      </p>
      <Orders orders={orders} />

      <OrderSelectionModal
        isOpen={showOrderSelectionModal}
        orders={orders}
        onSelectOrder={handleOrderSelection}
        onClose={() => setShowOrderSelectionModal(false)}
      />

      {selectedOrder && (
        <OrderEditDeliveryDetailsModal
          editMode={showDeliveryModal}
          order={selectedOrder}
          close={handleDeliveryModalClose}
          update={handleDeliveryUpdate}
        />
      )}
    </>
  );
}

export default withRouter(DashboardComponent);
